import type { SlotSchema } from './types';
import { cfHashId } from './cfHash';
import { parseFragment, serializeFragment } from './parseFragment';

/**
 * Parse HTML and return SlotSchema[] in document order for every element
 * with a data-slot attribute. Skips elements inside <cf-instance> ref
 * markers because those slots belong to the referenced Main, not this one.
 */
export function extractSlotSchemas(html: string): SlotSchema[] {
  const root = parseFragment(html);
  const out: SlotSchema[] = [];
  walkSkippingCfInstance(root, (el) => {
    const name = el.getAttribute('data-slot');
    if (name !== null) {
      out.push({
        name,
        defaultText: el.textContent ?? '',
        cfNodeId: el.getAttribute('data-cf-id') ?? '',
      });
    }
  });
  return out;
}

/**
 * Walk HTML and ensure every element has a stable data-cf-id attribute.
 * Existing/author ids are preserved untouched. New ids are minted by
 * `cfHashId` — a content hash over the node's tag/attrs/direct-text + its
 * parent-path + in-set index — so an id stays put when *other* nodes are
 * inserted, removed, or reordered around it (motion / comment / layer-select
 * targets survive edits). Collision-free: if a hash lands on a value already
 * in use, it is salted (`<hash>-1`, `<hash>-2`, …) until free.
 */
export function injectCfNodeIds(html: string): string {
  const root = parseFragment(html);
  const used = new Set<string>();
  walk(root, (el) => {
    const id = el.getAttribute('data-cf-id');
    if (id) used.add(id);
  });
  walk(root, (el) => {
    if (el.hasAttribute('data-cf-id')) return; // keep existing/author ids
    let id = cfHashId(el);
    // Collision-free guarantee: if a hash lands on an in-use value, salt until free.
    let salt = 0;
    while (used.has(id)) id = `${cfHashId(el)}-${++salt}`;
    used.add(id);
    el.setAttribute('data-cf-id', id);
  });
  return serializeFragment(root);
}

// ---- helpers (parse seam shared via ./parseFragment so every cf-id site agrees) ----

function walk(root: HTMLElement, fn: (el: Element) => void): void {
  for (const el of root.querySelectorAll('*')) fn(el);
}

function walkSkippingCfInstance(root: HTMLElement, fn: (el: Element) => void): void {
  for (const el of root.querySelectorAll('*')) {
    if (isInsideCfInstance(el)) continue;
    fn(el);
  }
}

function isInsideCfInstance(el: Element): boolean {
  if (el.tagName.toLowerCase() === 'cf-instance') return true;
  let p: Element | null = el.parentElement;
  while (p) {
    if (p.tagName.toLowerCase() === 'cf-instance') return true;
    p = p.parentElement;
  }
  return false;
}

/**
 * Resolve a node inside `root` by its cf-id, falling back to `data-cf-alias`.
 * The alias is written by the content-hash migration so references that still
 * carry an old positional id keep resolving for one release. Returns null if
 * neither matches.
 */
export function resolveCfNode(root: ParentNode, id: string): Element | null {
  if (typeof CSS !== 'undefined' && CSS.escape) {
    const esc = CSS.escape(id);
    return (
      root.querySelector(`[data-cf-id="${esc}"]`) ??
      root.querySelector(`[data-cf-alias="${esc}"]`)
    );
  }
  // Fallback (no CSS.escape, e.g. older jsdom): compare attribute values directly
  // so any metachar in `id` matches exactly without selector-escaping pitfalls.
  for (const el of root.querySelectorAll('[data-cf-id]')) {
    if (el.getAttribute('data-cf-id') === id) return el;
  }
  for (const el of root.querySelectorAll('[data-cf-alias]')) {
    if (el.getAttribute('data-cf-alias') === id) return el;
  }
  return null;
}
